import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Fab,
  Stack,
  InputAdornment,
  CircularProgress,
  TableSortLabel,
  FormControlLabel,
} from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  FilterList,
  FileDownload,
  DeleteSweep,
  Add,
  TrendingUp,
  TrendingDown,
  Refresh,
  CallSplit,
  ContentCut,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { transactionsApi, categoriesApi, analyticsApi, tagsApi, sharedExpensesApi, friendsApi, friendGroupsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate, formatDateTime } from '../utils/helpers';
import LoadingButton from '../components/LoadingButton';
import BulkEditModal from '../components/BulkEditModal';
import TagChip from '../components/TagChip';
import TagSelector from '../components/TagSelector';
import AddTransactionDialog from '../components/AddTransactionDialog';
import SplitTransactionDialog from '../components/SplitTransactionDialog';
import SplitTransactionAmountDialog from '../components/SplitTransactionAmountDialog';
import { TRANSACTION_KINDS, kindLabel, kindsFor, kindHelp } from '../utils/emailTypes';
import { categoryMenuItems } from '../utils/categoryMenu';

const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError, showInfo } = useNotification();

  // State management
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit')) || 25);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    type: searchParams.get('type') || '',
    kind: searchParams.get('kind') || '',
    recurring: searchParams.get('recurring') || '',
    category: searchParams.get('category') || '',
    accountType: searchParams.get('accountType') || '',
    accountId: searchParams.get('accountId') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    tags: searchParams.get('tags') || '',
  });
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, transaction: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, transaction: null });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [splitDialog, setSplitDialog] = useState({ open: false, transaction: null });
  const [splitAmountDialog, setSplitAmountDialog] = useState({ open: false, transaction: null });
  const [updatingCategory, setUpdatingCategory] = useState(null); // Track which transaction's category is being updated
  const [tagEditDialog, setTagEditDialog] = useState({ open: false, transaction: null });
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [tempSelectedTags, setTempSelectedTags] = useState([]);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc'); // Default to newest first

  // Debug: Component mount/unmount tracking
  useEffect(() => {
    console.log('📊 Transactions page MOUNTED');
    return () => {
      console.log('📊 Transactions page UNMOUNTED');
    };
  }, []);

  // Cleanup: Clear Transactions params when navigating away
  useEffect(() => {
    return () => {
      console.log('📊 Transactions cleanup - clearing params');
      // Use replaceState to avoid adding to browser history
      const url = new URL(window.location);
      url.search = ''; // Clear all params
      window.history.replaceState({}, '', url);
    };
  }, []);

  // Debug: Data loading tracking
  useEffect(() => {
    console.log('📊 Transactions useEffect triggered - loading data:', {
      page,
      rowsPerPage,
      filters,
      sortBy,
      sortOrder,
      currentURL: window.location.href,
      searchParamsString: searchParams.toString(),
      allParams: Object.fromEntries(searchParams),
      timestamp: new Date().toISOString()
    });
    loadTransactions();
    loadCategories();
    loadAccountIds();
    loadTags();
  }, [page, rowsPerPage, filters, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debug: Track page state changes
  useEffect(() => {
    console.log('📊 Page state changed:', {
      newPage: page,
      searchParamPage: searchParams.get('page'),
      timestamp: new Date().toISOString()
    });
  }, [page]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const apiParams = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        ...filters
      };
      
      // Remove empty filters from API params
      Object.keys(apiParams).forEach(key => {
        if (apiParams[key] === '' || apiParams[key] == null) {
          delete apiParams[key];
        }
      });

      const response = await transactionsApi.getAll(apiParams);
      setTransactions(response.data.transactions);
      setTotalCount(response.data.pagination.totalCount);
      setSummary(response.data.summary);
      
      // Update URL params with 0-based page (matching state)
      const urlParams = {
        page, // 0-based page for URL consistency
        limit: rowsPerPage,
        sortBy,
        sortOrder,
        ...filters
      };
      updateUrlParams(urlParams);
    } catch (error) {
      showError('Failed to load transactions');
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load account IDs
  const loadAccountIds = async () => {
    try {
      const response = await analyticsApi.getAccountIds();
      setAccountIds(response.data?.accountIds || []);
    } catch (error) {
      console.error('Error loading account IDs:', error);
      setAccountIds([]);
    }
  };

  // Load tags
  const loadTags = async () => {
    try {
      const response = await tagsApi.getAll();
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  const updateUrlParams = (params) => {
    // Only update URL if still on transactions page
    if (window.location.pathname === '/transactions') {
      const newParams = new URLSearchParams();
      // No prefix needed - each page manages its own params
      Object.entries(params).forEach(([key, value]) => {
        if (value && value !== '') {
          newParams.set(key, value);
        }
      });
      
      console.log('📊 Transactions BEFORE setSearchParams (updateUrlParams):', {
        currentURL: window.location.href,
        newParams: Object.fromEntries(newParams),
        rawParams: params
      });
      setSearchParams(newParams);
      console.log('📊 Transactions AFTER setSearchParams (updateUrlParams):', {
        newURL: window.location.href
      });
    } else {
      console.log('📊 Transactions skipping setSearchParams - user navigated to:', window.location.pathname);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: '',
      kind: '',
      recurring: '',
      category: '',
      accountType: '',
      accountId: '',
      startDate: '',
      endDate: '',
      tags: '',
    });
    setPage(0);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTransactions(transactions.map(t => t.id));
    } else {
      setSelectedTransactions([]);
    }
  };

  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleEditTransaction = async (transactionData) => {
    try {
      const response = await transactionsApi.update(editDialog.transaction.id, transactionData);
      showSuccess(response.data?.message || 'Transaction updated successfully');
      setEditDialog({ open: false, transaction: null });
      loadTransactions();
    } catch (error) {
      showError('Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async () => {
    try {
      await transactionsApi.delete(deleteDialog.transaction.id);
      showSuccess('Transaction deleted successfully');
      setDeleteDialog({ open: false, transaction: null });
      loadTransactions();
    } catch (error) {
      showError('Failed to delete transaction');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await transactionsApi.bulkDelete(selectedTransactions);
      showSuccess(`${selectedTransactions.length} transactions deleted successfully`);
      setBulkDeleteDialog(false);
      setSelectedTransactions([]);
      loadTransactions();
    } catch (error) {
      showError('Failed to delete transactions');
    }
  };

  const handleBulkEdit = async (updates) => {
    setBulkEditLoading(true);
    try {
      await transactionsApi.bulkUpdate(selectedTransactions, updates);
      showSuccess(`${selectedTransactions.length} transactions updated successfully`);
      setBulkEditDialog(false);
      setSelectedTransactions([]);
      loadTransactions();
    } catch (error) {
      showError('Failed to update transactions');
    } finally {
      setBulkEditLoading(false);
    }
  };

  // Handle manual transaction creation
  const handleAddTransaction = async (transactionData) => {
    try {
      await transactionsApi.create(transactionData);
      showSuccess('Transaction added successfully');
      loadTransactions();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to add transaction');
      throw error; // Re-throw so dialog can handle it
    }
  };

  const handleSplitTransaction = async (splitData) => {
    try {
      await sharedExpensesApi.fromTransaction(splitData);
      showSuccess('Transaction split successfully! Check Split Expenses page.');
      setSplitDialog({ open: false, transaction: null });
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to split transaction');
      throw error;
    }
  };

  const handleSplitTransactionAmount = async (splits) => {
    try {
      const response = await transactionsApi.splitTransaction(splitAmountDialog.transaction.id, { splits });
      showSuccess(`Transaction split into ${splits.length} transactions successfully!`);
      setSplitAmountDialog({ open: false, transaction: null });
      loadTransactions(); // Reload to show new transactions
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to split transaction');
      throw error;
    }
  };

  // Handle tag editing for individual transaction
  const handleOpenTagEdit = (transaction) => {
    setTempSelectedTags(transaction.tags || []); // Initialize with current tags
    setTagEditDialog({ open: true, transaction });
  };

  const handleCloseTagEdit = () => {
    setTagEditDialog({ open: false, transaction: null });
    setTempSelectedTags([]);
  };

  const handleSaveTransactionTags = async () => {
    if (savingTags) return; // Prevent multiple submissions
    
    setSavingTags(true);
    try {
      const tagIds = tempSelectedTags.map(tag => tag.id);
      await transactionsApi.update(tagEditDialog.transaction.id, { tagIds });
      showSuccess('Tags updated successfully');
      handleCloseTagEdit();
      loadTransactions();
    } catch (error) {
      showError('Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  // Handle inline category update
  const handleCategoryChange = async (transactionId, newCategory) => {
    setUpdatingCategory(transactionId);
    try {
      // Update the transaction with new category
      await transactionsApi.update(transactionId, { category: newCategory });
      showSuccess('Category updated successfully');
      loadTransactions(); // Refresh the transactions list
    } catch (error) {
      console.error('Error updating category:', error);
      showError('Failed to update category');
    } finally {
      setUpdatingCategory(null);
    }
  };

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to desc for date, asc for others
      setSortBy(column);
      setSortOrder(column === 'date' ? 'desc' : 'asc');
    }
  };

  const handleExport = async () => {
    try {
      const response = await transactionsApi.export({
        format: 'csv',
        filters: filters
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('Transactions exported successfully');
    } catch (error) {
      showError('Failed to export transactions');
    }
  };

  const getTypeColor = (type) => {
    return type === 'credit' ? 'success' : 'error';
  };

  const getAccountTypeLabel = (accountType) => {
    return accountType === 'bank_account' ? 'Bank' : 'Credit Card';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Transactions
            </Typography>
            {summary && (
              <Typography variant="body2" color="text.secondary">
                {summary.totalTransactions.toLocaleString()} transactions • 
                Income: {formatCurrency(summary.totalCredits)} • 
                Expenses: {formatCurrency(summary.totalExpenses)} • 
                Net: {formatCurrency(summary.netBalance)}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddDialog(true)}
              color="primary"
            >
              Add Transaction
            </Button>
            <LoadingButton
              variant="outlined"
              startIcon={<Refresh />}
              loading={loading}
              onClick={loadTransactions}
            >
              Refresh
            </LoadingButton>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Stack>
        </Box>

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {summary.totalTransactions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(summary.totalCredits)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Credits
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {formatCurrency(summary.totalExpenses)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Expenses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography 
                    variant="h4" 
                    color={summary.netBalance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(summary.netBalance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net Balance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        {showFilters && (
          <Card sx={{ mb: 4 }}>
            <CardHeader 
              title="Filter Transactions" 
              action={
                <Button onClick={clearFilters} size="small">
                  Clear All
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Search"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Search description..."
                    size="medium"
                    sx={{ minWidth: 250 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={2}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="credit">Credit</MenuItem>
                      <MenuItem value="expense">Expense</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={2}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
                    <InputLabel>Kind</InputLabel>
                    <Select
                      value={filters.kind}
                      onChange={(e) => handleFilterChange('kind', e.target.value)}
                      label="Kind"
                    >
                      <MenuItem value="">All Kinds</MenuItem>
                      {TRANSACTION_KINDS.map(k => (
                        <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={2}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 140 }}>
                    <InputLabel>Recurring</InputLabel>
                    <Select
                      value={filters.recurring}
                      onChange={(e) => handleFilterChange('recurring', e.target.value)}
                      label="Recurring"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">Recurring only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 180 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      label="Category"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categoryMenuItems(categories)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 180 }}>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={filters.accountType}
                      onChange={(e) => handleFilterChange('accountType', e.target.value)}
                      label="Account Type"
                    >
                      <MenuItem value="">All Accounts</MenuItem>
                      <MenuItem value="bank_account">Bank Account</MenuItem>
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 180 }}>
                    <InputLabel>Account ID</InputLabel>
                    <Select
                      value={filters.accountId}
                      onChange={(e) => handleFilterChange('accountId', e.target.value)}
                      label="Account ID"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">All Account IDs</MenuItem>
                      {accountIds.map((id) => (
                        <MenuItem key={id} value={id}>
                          {id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Start Date"
                    value={filters.startDate ? new Date(filters.startDate + 'T00:00:00') : null}
                    onChange={(date) => {
                      if (date) {
                        // Format date in local timezone to avoid timezone offset issues
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        handleFilterChange('startDate', `${year}-${month}-${day}`);
                      } else {
                        handleFilterChange('startDate', '');
                      }
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="End Date"
                    value={filters.endDate ? new Date(filters.endDate + 'T00:00:00') : null}
                    onChange={(date) => {
                      if (date) {
                        // Format date in local timezone to avoid timezone offset issues
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        handleFilterChange('endDate', `${year}-${month}-${day}`);
                      } else {
                        handleFilterChange('endDate', '');
                      }
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <FormControl fullWidth size="medium" sx={{ minWidth: 200 }}>
                    <InputLabel>Tags</InputLabel>
                    <Select
                      multiple
                      value={filters.tags ? filters.tags.split(',').filter(Boolean) : []}
                      onChange={(e) => handleFilterChange('tags', e.target.value.join(','))}
                      label="Tags"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((tagId) => {
                            const tag = tags.find(t => t.id === tagId);
                            return tag ? <TagChip key={tag.id} tag={tag} size="small" /> : null;
                          })}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select tags to filter by (OR logic)</em>
                      </MenuItem>
                      {tags.map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: tag.color,
                                flexShrink: 0
                              }}
                            />
                            {tag.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Bulk Actions */}
        {selectedTransactions.length > 0 && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setBulkEditDialog(true)}
                  startIcon={<Edit />}
                >
                  Edit Fields
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setBulkDeleteDialog(true)}
                  startIcon={<DeleteSweep />}
                >
                  Delete ({selectedTransactions.length})
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setSelectedTransactions([])}
                >
                  Clear
                </Button>
              </Stack>
            }
          >
            ⚡ {selectedTransactions.length} transaction(s) selected
          </Alert>
        )}

        {/* Transactions Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < transactions.length}
                      checked={transactions.length > 0 && selectedTransactions.length === transactions.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'date' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'date'}
                      direction={sortBy === 'date' ? sortOrder : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'description' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'description'}
                      direction={sortBy === 'description' ? sortOrder : 'asc'}
                      onClick={() => handleSort('description')}
                    >
                      Description
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'amount' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'amount'}
                      direction={sortBy === 'amount' ? sortOrder : 'asc'}
                      onClick={() => handleSort('amount')}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'type' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'type'}
                      direction={sortBy === 'type' ? sortOrder : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell sortDirection={sortBy === 'bank' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'bank'}
                      direction={sortBy === 'bank' ? sortOrder : 'asc'}
                      onClick={() => handleSort('bank')}
                    >
                      Bank
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'accountId' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'accountId'}
                      direction={sortBy === 'accountId' ? sortOrder : 'asc'}
                      onClick={() => handleSort('accountId')}
                    >
                      Account ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'accountType' ? sortOrder : false}>
                    <TableSortLabel
                      active={sortBy === 'accountType'}
                      direction={sortBy === 'accountType' ? sortOrder : 'asc'}
                      onClick={() => handleSort('accountType')}
                    >
                      Account Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length > 0 ? (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTransactions.includes(transaction.id)}
                          onChange={() => handleSelectTransaction(transaction.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(transaction.date)}
                        </Typography>
                        {transaction.time && (
                          <Typography variant="caption" color="text.secondary">
                            {transaction.time}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Original Description:
                              </Typography>
                              <Typography variant="body2">
                                {transaction.originalDescription || transaction.description || 'N/A'}
                              </Typography>
                            </Box>
                          } 
                          arrow 
                          placement="top"
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'help'
                            }}
                          >
                            {transaction.description}
                          </Typography>
                        </Tooltip>
                        {transaction.bank && (
                          <Chip 
                            label={transaction.bank} 
                            size="small" 
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="h6" 
                          color={getTypeColor(transaction.type)}
                          fontWeight="bold"
                        >
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          color={getTypeColor(transaction.type)}
                          size="small"
                          icon={transaction.type === 'credit' ? <TrendingUp /> : <TrendingDown />}
                        />
                        {transaction.kind && (
                          <Tooltip title={kindHelp(transaction.kind)}>
                            <Chip
                              label={kindLabel(transaction.kind)}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }}
                            />
                          </Tooltip>
                        )}
                        {transaction.recurringSeriesId && (
                          <Tooltip title="Part of a recurring payment. See Upcoming for what's due next.">
                            <Chip
                              label="Recurring"
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ mt: 0.5, display: 'flex', width: 'fit-content' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={transaction.category || ''}
                            onChange={(e) => handleCategoryChange(transaction.id, e.target.value)}
                            disabled={updatingCategory === transaction.id}
                            displayEmpty
                            size="small"
                            sx={{
                              '& .MuiSelect-select': {
                                py: 0.5,
                                fontSize: '0.875rem',
                              },
                            }}
                            endAdornment={
                              updatingCategory === transaction.id && (
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                              )
                            }
                          >
                            <MenuItem value="">
                              <em>No Category</em>
                            </MenuItem>
                            {categoryMenuItems(categories)}
                            {/* Old (hidden) categories still show on past transactions */}
                            {transaction.category && !categories.some(c => c.name === transaction.category) && (
                              <MenuItem value={transaction.category}>{transaction.category}</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.bank || 'Unknown'} 
                          size="small" 
                          color="default"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {transaction.accountId || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getAccountTypeLabel(transaction.accountType)} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={
                            <Box>
                              {transaction.tags && transaction.tags.length > 0 ? (
                                <>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    Tags:
                                  </Typography>
                                  {transaction.tags.map(tag => (
                                    <Box key={tag.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: tag.color
                                        }}
                                      />
                                      <Typography variant="caption">{tag.name}</Typography>
                                    </Box>
                                  ))}
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', opacity: 0.8 }}>
                                    Click to edit
                                  </Typography>
                                </>
                              ) : (
                                'Click to add tags'
                              )}
                            </Box>
                          }
                          arrow
                          placement="left"
                        >
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8
                              }
                            }}
                            onClick={() => handleOpenTagEdit(transaction)}
                          >
                            {transaction.tags && transaction.tags.length > 0 ? (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                  {transaction.tags.slice(0, 3).map((tag, index) => (
                                    <Box
                                      key={tag.id}
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: tag.color,
                                        border: '2px solid white',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        marginLeft: index > 0 ? '-6px' : 0,
                                        zIndex: 3 - index,
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                          transform: 'scale(1.1)',
                                          zIndex: 10
                                        }
                                      }}
                                    />
                                  ))}
                                </Box>
                                {transaction.tags.length > 3 && (
                                  <Chip 
                                    label={`+${transaction.tags.length - 3}`}
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold'
                                    }}
                                  />
                                )}
                                {transaction.tags.length <= 3 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    {transaction.tags.length}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <IconButton size="small" sx={{ p: 0.5 }}>
                                <Add fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {transaction.knownBalance != null ? (
                          <Tooltip title={`Balance as of ${transaction.balanceDate || transaction.date}`}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontWeight: 500,
                                color: transaction.knownBalance >= 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              {formatCurrency(transaction.knownBalance)}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {transaction.type === 'expense' && (
                            <>
                              <Tooltip title="Split with friends">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setSplitDialog({ open: true, transaction })}
                                >
                                  <CallSplit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Split amount">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => setSplitAmountDialog({ open: true, transaction })}
                                >
                                  <ContentCut />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => setEditDialog({ open: true, transaction })}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, transaction })}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', py: 8 }}>
                      {loading ? (
                        <Typography>Loading transactions...</Typography>
                      ) : (
                        <Box>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No transactions found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Upload some bank statements to get started
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Card>

        {/* Edit Transaction Dialog */}
        <EditTransactionDialog
          open={editDialog.open}
          transaction={editDialog.transaction}
          categories={categories}
          onClose={() => setEditDialog({ open: false, transaction: null })}
          onSave={handleEditTransaction}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, transaction: null })}>
          <DialogTitle>Delete Transaction</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this transaction? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, transaction: null })}>
              Cancel
            </Button>
            <Button onClick={handleDeleteTransaction} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
          <DialogTitle>Delete {selectedTransactions.length} Transactions</DialogTitle>
          <DialogContent>
            Are you sure you want to delete {selectedTransactions.length} selected transactions? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkDelete} color="error" variant="contained">
              Delete All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Edit Modal */}
        <BulkEditModal
          open={bulkEditDialog}
          onClose={() => setBulkEditDialog(false)}
          onSave={handleBulkEdit}
          selectedCount={selectedTransactions.length}
          categories={categories.map(cat => cat.name)}
          loading={bulkEditLoading}
        />

        {/* Tag Edit Dialog */}
        <Dialog 
          open={tagEditDialog.open} 
          onClose={!savingTags ? handleCloseTagEdit : undefined}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Transaction Tags</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TagSelector
                value={tempSelectedTags}
                onChange={setTempSelectedTags}
                allTags={tags}
                label="Transaction Tags"
                placeholder="Select multiple tags..."
                disabled={savingTags}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                You can select multiple tags at once. Click Save when done.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTagEdit} disabled={savingTags}>
              Cancel
            </Button>
            <LoadingButton
              onClick={handleSaveTransactionTags}
              variant="contained"
              loading={savingTags}
            >
              Save Tags
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Add Transaction Dialog */}
        <AddTransactionDialog
          open={addDialog}
          onClose={() => setAddDialog(false)}
          onAdd={handleAddTransaction}
          categories={categories}
          tags={tags}
        />

        {/* Split Transaction Dialog */}
        <SplitTransactionDialog
          open={splitDialog.open}
          transaction={splitDialog.transaction}
          onClose={() => setSplitDialog({ open: false, transaction: null })}
          onSuccess={handleSplitTransaction}
        />

        {/* Split Transaction Amount Dialog */}
        <SplitTransactionAmountDialog
          open={splitAmountDialog.open}
          transaction={splitAmountDialog.transaction}
          categories={categories}
          tags={tags}
          onClose={() => setSplitAmountDialog({ open: false, transaction: null })}
          onSplit={handleSplitTransactionAmount}
        />
      </Box>
    </LocalizationProvider>
  );
};

// Edit Transaction Dialog Component
const EditTransactionDialog = ({ open, transaction, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'expense',
    category: '',
    date: '',
    time: '',
    kind: ''
  });
  const [rememberForPayee, setRememberForPayee] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transaction) {
      setFormData({
        description: transaction.description || '',
        amount: transaction.amount || '',
        type: transaction.type || 'expense',
        category: transaction.category || '',
        date: transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : '',
        time: transaction.time || '',
        kind: transaction.kind || ''
      });
      setRememberForPayee(false);
    }
  }, [transaction]);

  const kindChanged = !!formData.kind && formData.kind !== (transaction?.kind || '');
  const categoryChanged = formData.category !== (transaction?.category || '');
  const payeeName = transaction?.payee || transaction?.description;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // The kind is sent only when changed: a kind sent is a kind the user set
      const { kind, ...data } = formData;
      if (kindChanged) data.kind = kind;
      if (rememberForPayee && (kindChanged || categoryChanged)) data.rememberForPayee = true;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Transaction</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  label="Type"
                >
                  <MenuItem value="credit">Credit</MenuItem>
                  <MenuItem value="expense">Expense</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time (Optional)"
                placeholder="HH:MM"
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                >
                  <MenuItem value="">No Category</MenuItem>
                  {categoryMenuItems(categories)}
                  {formData.category && !categories.some(c => c.name === formData.category) && (
                    <MenuItem value={formData.category}>{formData.category}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Kind</InputLabel>
                <Select
                  value={formData.kind}
                  onChange={(e) => setFormData(prev => ({ ...prev, kind: e.target.value }))}
                  label="Kind"
                >
                  {kindsFor(formData.type).map(k => (
                    <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Only "Spending" counts towards spending and budgets; transfers between your accounts,
                investments and refunds don't.
              </Typography>
            </Grid>
            {(kindChanged || categoryChanged) && payeeName && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={rememberForPayee} onChange={(e) => setRememberForPayee(e.target.checked)} />}
                  label={`Always do this for "${payeeName}" (updates its other transactions too)`}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={saving}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Transactions;